import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import config from '@/config';
import { AUTH_HEADER, HTTP_STATUS } from '@/config/api.config';
import { API_TIMEOUT } from '@/shared/constants/timing';
import { STORAGE_KEYS } from '@/shared/constants/storage.constants';
import { getItem, updateAccessToken, clearUserCookies } from '@/shared/utils/storage';
import { store } from '@/app/store';
import { setOrgAccessToken } from '@/features/auth/slices/authSlice';
import { setUserOrganisation } from '@/features/auth/api/authApi';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

const createApiClient = (): AxiosInstance => {
  const apiClient = axios.create({
    baseURL: config.get('API_BASE_URL'),
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  apiClient.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const token = getItem(STORAGE_KEYS.ORG_ACCESS_TOKEN);

      if (token && !requestConfig.headers[AUTH_HEADER]) {
        requestConfig.headers[AUTH_HEADER] = `Bearer ${token}`;
      }

      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 and token refresh
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      if (status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers[AUTH_HEADER] = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { auth } = store.getState();
          const { user_refresh_token = '', selected_org_id = '' } = auth;

          if (!user_refresh_token || !selected_org_id) {
            throw new Error('No refresh token or org ID');
          }

          const orgAuthData = await setUserOrganisation({
            orgId: selected_org_id,
            refreshToken: user_refresh_token,
          });

          const newToken = orgAuthData.access_token || '';

          updateAccessToken({ org_access_token: newToken });
          store.dispatch(setOrgAccessToken(newToken));

          processQueue(null, newToken);

          originalRequest.headers[AUTH_HEADER] = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearUserCookies();
          redirectToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return apiClient;
};

export const redirectToLogin = (pathname?: string): void => {
  const loginUrl = config.get<string>('services.login');
  const source = window.location.host + (pathname && pathname.split('/').length > 2 ? pathname : '');
  const path = `${loginUrl}?source=${encodeURIComponent(source)}`;
  window.location.replace(path);
};

export const apiClient = createApiClient();
export default apiClient;
